// Mock data for Life Stories Therapy website - Matching original site content

export const siteConfig = {
  name: "Life Stories Therapy",
  tagline: "Because every story still matters",
  description: "Honoring and preserving the God-given dignity, identity, and life stories of individuals living with dementia through compassionate, faith-filled care.",
  email: "info@lifestoriestherapy.com",
  phone: "Available upon request",
  location: "In-home visits & virtual sessions available",
  responseTime: "Within 24-48 hours",
  founderName: "Lacey"
};

export const navigation = [
  { name: "Home", href: "/" },
  { name: "About", href: "/about" },
  { name: "Services", href: "/services" },
  { name: "Values", href: "/values" },
  { name: "Contact", href: "/contact" }
];

export const heroQuote = {
  text: "Even to your old age and gray hairs I am He, I am He who will sustain you.",
  reference: "Isaiah 46:4"
};

export const missionSection = {
  title: "Affirming that every story still matters — at every stage of life",
  content: "Life Stories Therapy exists to honor and preserve the God-given dignity, identity, and life stories of individuals living with dementia. Through compassionate, evidence-based therapy and a faith-filled presence, we walk alongside individuals and families through every season of the journey.",
  quote: {
    text: "I have called you by name; you are mine.",
    reference: "Isaiah 43:1"
  }
};

export const services = [
  {
    id: 1,
    title: "Dementia Therapy",
    description: "Evidence-based, person-centered therapy that honors the whole person, supporting well-being while preserving dignity and connection.",
    icon: "BookOpen"
  },
  {
    id: 2,
    title: "Death Doula Services",
    description: "A faithful, compassionate presence walking alongside individuals and families through end-of-life with grace, comfort, and hope.",
    icon: "Users"
  }
];

export const servicesPageData = {
  hero: {
    subtitle: "Our Services",
    title: "Compassionate Care for Every Season",
    description: "Evidence-based therapy and faithful presence, walking alongside you and your loved ones with dignity and hope."
  },
  dementiaTherapy: {
    title: "Dementia Therapy",
    subtitle: "Honoring the Whole Person Through Therapeutic Care",
    description: "Our dementia therapy services are designed to support individuals living with dementia while honoring their identity, dignity, and life story. Using evidence-based approaches and a person-centered philosophy, we create meaningful connections that enhance well-being at every stage.",
    offerings: [
      "Person-centered, individualized care plans",
      "Life story preservation and reminiscence therapy",
      "Cognitive stimulation and engagement activities",
      "Emotional and spiritual support",
      "Family education and caregiver support",
      "Home-based sessions available"
    ],
    features: [
      {
        title: "Meaningful Connection",
        description: "Creating moments of joy and recognition through personalized engagement"
      },
      {
        title: "Comfortable Setting",
        description: "Services provided in the comfort of home or care facility"
      },
      {
        title: "Family Partnership",
        description: "Empowering families with tools, education, and support"
      }
    ],
    quote: {
      text: "Whatever you do, work at it with all your heart, as working for the Lord.",
      reference: "Colossians 3:23"
    }
  },
  deathDoula: {
    title: "Death Doula Services",
    subtitle: "A Faithful Presence at Life's Sacred Threshold",
    description: "Death doula services provide non-medical, emotional, and spiritual support for individuals and families as they approach end of life. As a death doula, Lacey offers a compassionate, faithful presence — helping to honor final wishes, create meaning, and navigate this sacred journey with dignity and peace.",
    offerings: [
      "End-of-life planning and preparation",
      "Emotional and spiritual companionship",
      "Legacy projects and life reflection",
      "Vigil presence and comfort care support",
      "Family guidance and communication support",
      "Post-death family support and resources"
    ],
    features: [
      {
        title: "Compassionate Presence",
        description: "A calm, supportive presence during the most sacred of transitions"
      },
      {
        title: "Honoring Wishes",
        description: "Ensuring personal, spiritual, and family preferences are respected"
      },
      {
        title: "Family Support",
        description: "Guiding and supporting families through each step with grace"
      }
    ]
  },
  approach: {
    title: "What Makes Our Care Different",
    description: "Every service we provide is grounded in our core values and delivered with the same compassion and dignity we would want for our own families.",
    items: [
      {
        title: "Faith-Filled",
        description: "Our care is rooted in faith, offering spiritual comfort and hope throughout the journey."
      },
      {
        title: "Evidence-Based",
        description: "We use proven therapeutic approaches combined with personalized, intuitive care."
      },
      {
        title: "Family-Centered",
        description: "We partner with families, providing education, support, and resources every step of the way."
      }
    ],
    quote: {
      text: "The Lord is close to the brokenhearted and saves those who are crushed in spirit.",
      reference: "Psalm 34:18"
    }
  },
  cta: {
    title: "Ready to Take the Next Step?",
    description: "Whether you're seeking dementia therapy or death doula services, Lacey is here to walk alongside you with compassion, faith, and hope. Reach out today to start the conversation.",
    buttonText: "Schedule a Consultation"
  }
};

export const valuesData = [
  {
    number: "01",
    title: "Dignity",
    description: "We believe every person is created with inherent worth and deserves to be treated with honor, respect, and compassion at every stage of life.",
    quote: {
      text: "So God created mankind in His own image.",
      reference: "Genesis 1:27"
    },
    affirmation: "Every person retains God-given worth, regardless of cognitive changes.",
    icon: "Crown"
  },
  {
    number: "02",
    title: "Story",
    description: "Each life carries meaning. We honor personal histories, experiences, and identities, using life stories as a foundation for connection and therapeutic care.",
    quote: {
      text: "Your eyes saw my unformed body; all the days ordained for me were written in your book.",
      reference: "Psalm 139:16"
    },
    affirmation: "Each life is known fully by God and holds enduring meaning.",
    icon: "BookOpen"
  },
  {
    number: "03",
    title: "Faithful Presence",
    description: "We serve with a steady, compassionate presence — walking alongside individuals and families with patience, grace, and hope, even when words and memories fade.",
    quote: {
      text: "The Lord is close to the brokenhearted.",
      reference: "Psalm 34:18"
    },
    affirmation: "God's nearness models the presence we offer to those we serve.",
    icon: "Heart"
  },
  {
    number: "04",
    title: "Compassionate Care",
    description: "Our therapy is rooted in empathy, gentleness, and understanding, responding to the emotional, spiritual, and relational needs of those we serve.",
    quote: {
      text: "Be kind and compassionate to one another.",
      reference: "Ephesians 4:32"
    },
    affirmation: "We respond with gentleness, empathy, and love.",
    icon: "HandHeart"
  },
  {
    number: "05",
    title: "Excellence in Therapy",
    description: "We are committed to providing evidence-based, ethical, and high-quality dementia therapy that supports well-being while honoring the whole person.",
    quote: {
      text: "Whatever you do, work at it with all your heart, as working for the Lord.",
      reference: "Colossians 3:23"
    },
    affirmation: "We pursue high-quality, ethical, and thoughtful care.",
    icon: "Star"
  },
  {
    number: "06",
    title: "Family and Community",
    description: "We value the role of families and caregivers, offering support, education, and partnership throughout the dementia journey.",
    quote: {
      text: "Carry each other's burdens.",
      reference: "Galatians 6:2"
    },
    affirmation: "Care is shared, and no family walks this journey alone.",
    icon: "Users"
  },
  {
    number: "07",
    title: "Hope",
    description: "Grounded in faith, we believe meaning, connection, and purpose remain possible. We choose hope in every season and affirm that every story still matters.",
    quote: {
      text: "May the God of hope fill you with all joy and peace as you trust in Him.",
      reference: "Romans 15:13"
    },
    affirmation: "Hope remains present in every season.",
    icon: "Sun"
  }
];

export const aboutData = {
  hero: {
    subtitle: "About Life Stories Therapy",
    title: "A Calling Born From Love",
    description: "Where personal experience meets professional dedication in service to those living with dementia"
  },
  mainQuote: {
    text: "Every person is known by God, every life holds meaning, and every story still matters."
  },
  certification: {
    title: "Certified & Trained",
    description: "Professional education and certification in dementia therapy services, grounded in both evidence-based practice and lived experience."
  },
  founder: {
    sectionTitle: "Meet Lacey",
    title: "The Heart Behind Life Stories Therapy",
    bio: [
      "Life Stories Therapy was founded by Lacey, whose journey into dementia care began not as a career plan, but as a deeply personal calling.",
      "In her 50's, Lacey became the primary caregiver for both her mother and father as they navigated dementia. Walking alongside them through the daily challenges, changes, and heartache gave her an intimate understanding of the emotional, physical, and spiritual toll dementia places on individuals and families.",
      "Through this season, Lacey witnessed firsthand how deeply a person's identity and life story still matter — long after memories begin to fade.",
      "After the passing of her mother, Lacey felt a clear calling to help others walking a similar path. Anchored by her trust and faith in God, she stepped forward in obedience, pursuing education, training, and certification in dementia therapy services. What began as caregiving became a mission to serve.",
      "Today, Lacey brings not only professional knowledge, but lived experience, compassion, and a faith-filled presence to her work. Her approach honors dignity, values life stories, and offers hope and support to individuals and families navigating the dementia journey."
    ],
    belief: "Life Stories Therapy exists because of this belief:",
    beliefStatement: "Every person is known by God, every life holds meaning, and every story still matters."
  },
  quote: {
    text: "Your eyes saw my unformed body; all the days ordained for me were written in your book.",
    reference: "Psalm 139:16"
  },
  cta: {
    title: "Let's Walk This Journey Together",
    description: "If you or a loved one is navigating the dementia journey, Lacey is here to help — with compassion, understanding, and hope."
  }
};

export const contactData = {
  hero: {
    subtitle: "Get In Touch",
    title: "Let's Connect",
    description: "Whether you have questions or are ready to begin, Lacey is here to listen and help guide you on this journey."
  },
  intro: {
    title: "Reach Out With Confidence",
    description: "Every conversation begins with listening. Share your story, ask your questions, or simply introduce yourself. There's no obligation — just compassion."
  },
  laceyNote: {
    title: "A Note from Lacey",
    text: "I understand how difficult it can be to reach out. Please know that every message is received with care, and there is no question too small. I'm honored to walk this path alongside you."
  },
  formNote: "Your information is kept confidential and never shared.",
  quote: {
    text: "Carry each other's burdens, and in this way you will fulfill the law of Christ.",
    reference: "Galatians 6:2"
  },
  serviceOptions: [
    { value: "dementia", label: "Dementia Therapy" },
    { value: "doula", label: "Death Doula Services" },
    { value: "both", label: "Both Services" },
    { value: "general", label: "General Inquiry" }
  ]
};

export const ctaSection = {
  title: "Begin Your Journey With Us",
  description: "Whether you're seeking support for yourself or a loved one, we're here to walk alongside you with compassion, dignity, and hope.",
  buttonText: "Connect With Us"
};

export const visionStatement = "We envision a world where individuals living with dementia are seen, known, and valued for who they are — not defined by memory loss but honored for their life stories.";

export const footerLinks = {
  quickLinks: [
    { name: "Home", href: "/" },
    { name: "About", href: "/about" },
    { name: "Services", href: "/services" },
    { name: "Values", href: "/values" },
    { name: "Contact", href: "/contact" }
  ]
};
